import { useState, useEffect } from "react";
import {
  Home,
  Calendar,
  Building2,
  X,
  Link as LinkIcon,
  Settings,
  Bell,
  Smartphone,
  ChevronDown,
  ChevronRight,
  Menu,
  User,
  LogOut,
  MessageCircle,
  Mic,
  Gift,
  UserPlus,
  LogIn,
  Loader2,
} from "lucide-react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { Link, usePage, router } from "@inertiajs/react";
import { Toaster } from "sonner";
import Footer from "@/components/Footer";
import { isNativeApp } from "@/lib/capacitor";
import { NotificationPermissionDialog } from "@/components/notifications/NotificationPermissionDialog";
import { BottomTabBar } from "@/components/layouts/BottomTabBar";
import { useSyncGuestDiagnostic } from "@/hooks/useSyncGuestDiagnostic";
import { UserAvatar, type UserGender } from "@/components/ui/user-avatar";
import { LineFriendPromptModal } from "@/components/users/LineFriendPromptModal";

const buildNavigation = (interviewRequestEnabled: boolean) => {
  const settingsChildren: { name: string; href: string; icon: any }[] = [
    {
      name: "通知",
      href: "/users/settings/notifications",
      icon: Bell,
    },
  ];

  if (isNativeApp()) {
    settingsChildren.push({
      name: "アプリ通知",
      href: "/users/settings/app_notifications",
      icon: Smartphone,
    });
  }

  const items: any[] = [
    {
      name: "ホーム",
      href: "/users",
      icon: Home,
    },
    {
      name: "面接練習",
      href: "/users/interview_practices",
      icon: Mic,
    },
    {
      name: "スケジュール",
      href: "/users/schedules",
      icon: Calendar,
    },
    {
      name: "選考企業",
      href: "/users/companies",
      icon: Building2,
    },
  ];

  if (interviewRequestEnabled) {
    items.push({
      name: "面談申し込み",
      href: "/users/interview_requests",
      icon: MessageCircle,
    });
  }

  items.push(
    {
      name: "サービス連携",
      href: "/users/integrations",
      icon: LinkIcon,
    },
    {
      name: "登録者プレゼント",
      href: "/users/presents",
      icon: Gift,
    },
    {
      name: "設定",
      icon: Settings,
      children: settingsChildren,
    }
  );

  return items;
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { url, props } = usePage<{
    current_user_header?: {
      nickname: string;
      profileImage: string | null;
      gender: UserGender;
    } | null;
    interview_request_feature_enabled?: boolean;
    [key: string]: unknown;
  }>();
  const currentUserHeader = props.current_user_header;
  const interviewRequestEnabled = props.interview_request_feature_enabled ?? false;
  const navigation = buildNavigation(interviewRequestEnabled);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [navigating, setNavigating] = useState(false);
  useSyncGuestDiagnostic();

  useEffect(() => {
    const startHandler = router.on("start", () => setNavigating(true));
    const finishHandler = router.on("finish", () => setNavigating(false));
    return () => {
      startHandler();
      finishHandler();
    };
  }, []);

  const currentPath = url;
  const pageProps = props as Record<string, unknown>;
  const isPublicInterviewPage = currentPath.startsWith("/interview_practices/");
  const isLoggedInOnPublicPage = Boolean(pageProps.loggedIn);
  const usePublicGuestNavigation = isPublicInterviewPage && !isLoggedInOnPublicPage;
  const signUpPath =
    typeof pageProps.signUpPath === "string" ? pageProps.signUpPath : "/users/sign_up";
  const signInPath =
    typeof pageProps.signInPath === "string" ? pageProps.signInPath : "/users/sign_in";

  const isActive = (path?: string) => path && currentPath === path;

  const isParentActive = (item: any) => {
    if (item.href) return isActive(item.href);
    if (item.children) {
      return item.children.some((child: any) => isActive(child.href));
    }
    return false;
  };

  const toggleExpand = (itemName: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemName)
        ? prev.filter((name) => name !== itemName)
        : [...prev, itemName]
    );
  };

  const navigationWithCurrent = navigation.map((item) => ({
    ...item,
    current: isParentActive(item),
    children: item.children?.map((child) => ({
      ...child,
      current: isActive(child.href),
    })),
  }));
  const publicGuestNavigation = [
    { name: "新規登録", href: signUpPath, icon: UserPlus, current: isActive(signUpPath) },
    { name: "ログイン", href: signInPath, icon: LogIn, current: isActive(signInPath) },
  ];
  const visibleNavigation = usePublicGuestNavigation ? publicGuestNavigation : navigationWithCurrent;

  const handleLogout = () => {
    setProfileMenuOpen(false);
    router.delete("/users/sign_out");
  };

  const handleNavLinkClick = () => {
    setSidebarOpen(false);
  };

  const handleProfileLinkClick = () => {
    setProfileMenuOpen(false);
  };

  const renderNavItem = (item: any, prefix: string) => (
    <li key={`${prefix}-${item.name}`}>
      {item.children ? (
        <>
          <button
            onClick={() => toggleExpand(item.name)}
            className={classNames(
              item.current
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
              "group flex w-full items-center justify-between gap-x-3 rounded-md p-2 text-sm font-medium"
            )}
          >
            <div className="flex gap-x-3">
              <item.icon aria-hidden="true" className="size-6 shrink-0" />
              {item.name}
            </div>
            {expandedItems.includes(item.name) ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
          </button>
          {expandedItems.includes(item.name) && (
            <ul className="mt-1 ml-9 space-y-1">
              {item.children.map((child: any, childIndex: number) => (
                <li key={`${prefix}-child-${child.name}-${childIndex}`}>
                  <Link
                    href={child.href}
                    onClick={handleNavLinkClick}
                    className={classNames(
                      child.current
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                      "group flex gap-x-3 rounded-md p-2 text-sm font-medium"
                    )}
                  >
                    <child.icon aria-hidden="true" className="size-5 shrink-0" />
                    {child.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <Link
          href={item.href}
          onClick={handleNavLinkClick}
          className={classNames(
            item.current
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
            "group flex gap-x-3 rounded-md p-2 text-sm font-medium"
          )}
        >
          <item.icon aria-hidden="true" className="size-6 shrink-0" />
          {item.name}
        </Link>
      )}
    </li>
  );

  return (
    <>
      {/* Mobile sidebar */}
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="-m-2.5 p-2.5"
              >
                <span className="sr-only">Close sidebar</span>
                <X aria-hidden="true" className="size-6 text-white" />
              </button>
            </div>

            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-background px-6 pb-4 pt-[env(safe-area-inset-top)]">
              <div className="flex h-16 shrink-0 items-center">
                <Link href="/users">
                  <img src="/imgs/logo.png" alt="Ukarte" className="h-8 w-auto" />
                </Link>
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2 space-y-1">
                      {visibleNavigation.map((item) =>
                        renderNavItem(item, "mobile")
                      )}
                    </ul>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-border bg-background px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center">
            <Link href="/users">
              <img src="/imgs/logo.png" alt="Ukarte" className="h-8 w-auto" />
            </Link>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {visibleNavigation.map((item) =>
                    renderNavItem(item, "desktop")
                  )}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72 min-h-screen flex flex-col">
        {/* Navigation header */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-border bg-background px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8 mt-[env(safe-area-inset-top)]">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-muted-foreground lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>

          <div
            className="h-6 w-px bg-border lg:hidden"
            aria-hidden="true"
          />

          {/* Mobile center logo */}
          <Link href="/users" className="absolute left-1/2 -translate-x-1/2 flex items-center lg:hidden">
            <img src="/imgs/logo.png" alt="Ukarte" className="h-6 w-auto" />
          </Link>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-x-4 lg:gap-x-6 ml-auto">
              {!usePublicGuestNavigation && (
                <>
                  <button
                    type="button"
                    className="-m-2.5 p-2.5 text-muted-foreground hover:text-foreground"
                  >
                    <span className="sr-only">View notifications</span>
                    <Bell className="h-6 w-6" aria-hidden="true" />
                  </button>

                  <div
                    className="hidden lg:block lg:h-6 lg:w-px lg:bg-border"
                    aria-hidden="true"
                  />
                </>
              )}

              {/* Profile */}
              <div className="relative flex items-center">
                <button
                  type="button"
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-2 rounded-full hover:bg-muted/80 transition-colors pr-2 pl-0.5 py-0.5"
                >
                  <UserAvatar
                    profileImage={currentUserHeader?.profileImage}
                    gender={currentUserHeader?.gender}
                    sizeClassName="size-9"
                  />
                  {currentUserHeader?.nickname && (
                    <span className="hidden lg:block text-sm font-medium text-foreground max-w-[120px] truncate">
                      {currentUserHeader.nickname}
                    </span>
                  )}
                </button>

                {profileMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setProfileMenuOpen(false)}
                    />
                    <div className="absolute right-0 top-10 z-50 w-48 bg-background border border-border rounded-md shadow-lg">
                      <div className="py-1">
                        {usePublicGuestNavigation ? (
                          <>
                            <Link
                              href={signUpPath}
                              onClick={handleProfileLinkClick}
                              className="flex w-full items-center gap-x-3 px-4 py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            >
                              <UserPlus className="size-4" />
                              新規登録
                            </Link>
                            <Link
                              href={signInPath}
                              onClick={handleProfileLinkClick}
                              className="flex w-full items-center gap-x-3 px-4 py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            >
                              <LogIn className="size-4" />
                              ログイン
                            </Link>
                          </>
                        ) : (
                          <>
                            <Link
                              href="/users/profile/edit"
                              onClick={handleProfileLinkClick}
                              className="flex w-full items-center gap-x-3 px-4 py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            >
                              <User className="size-4" />
                              プロフィール編集
                            </Link>
                            <button
                              onClick={handleLogout}
                              className="flex w-full items-center gap-x-3 px-4 py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            >
                              <LogOut className="size-4" />
                              ログアウト
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 bg-background pb-16 lg:pb-0 relative">
          {children}
          {navigating && (
            <div className="absolute inset-0 bg-background/60 flex items-center justify-center z-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
        </main>

        <div className="hidden lg:block">
          <Footer />
        </div>
      </div>

      <BottomTabBar />
      <Toaster position="top-right" richColors closeButton />
      <NotificationPermissionDialog />
      <LineFriendPromptModal />
    </>
  );
}
